import { useState, useEffect } from 'react';
import { candidateServiceApi, CandidateProfile } from '../services/candidateServiceApi';
import { authApiService } from '../../shared/auth/services/authApi';

/**
 * Unified hook to fetch and manage candidate profile data
 * Uses the /candidate/v1/me endpoint which extracts user from JWT token
 * This includes: name, email, phone, skills, experience[], education[], resume URL, etc.
 */
export const useCandidateData = () => {
    const [profile, setProfile] = useState<CandidateProfile | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = async (force = false) => {
        // Get user from auth to verify authentication
        const user = authApiService.getStoredUser();
        if (!user) {
            setError('User not authenticated');
            return;
        }

        // Don't refetch if we already have data (unless force=true)
        if (profile && !force) {
            return profile;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Use the /me endpoint - no ID needed, JWT token provides user identity
            console.log('[useCandidateData] Fetching profile using /me endpoint');

            const response = await candidateServiceApi.getCandidateProfile();

            console.log('[useCandidateData] Profile response:', response);

            if (response.success && response.candidate) {
                setProfile(response.candidate);
                return response.candidate;
            } else {
                const errorMsg = response.error || 'Failed to fetch profile';
                setError(errorMsg);
                console.error('[useCandidateData] Error:', errorMsg);
                return null;
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to fetch candidate profile';
            setError(errorMsg);
            console.error('[useCandidateData] Exception:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const refreshProfile = () => {
        return fetchProfile(true);
    };

    const clearProfile = () => {
        setProfile(null);
        setError(null);
    };

    // Auto-fetch on mount
    useEffect(() => {
        fetchProfile();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        profile,
        isLoading,
        error,
        fetchProfile,
        refreshProfile,
        clearProfile,
        hasProfile: !!profile,
    };
};
